import { useEffect, useState } from 'react';
import { SHOP_MODULES } from '@dunea/shared';
import { apiUpdateShopSettings, type Shop, type ShopSettingsPatch } from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import { isValidCurrency } from '~/components/workbench/tools/payments/payments-model';
import {
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  Section,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import {
  apiErrorMessage,
  isValidEmail,
  minorToMajorInput,
  moduleLabel,
  parseCount,
  parseMoney,
  previewSku,
  SHOP_MODULE_DESCRIPTIONS,
} from './shop-model';

interface FormState {
  name: string;
  currency: string;
  taxInclusive: boolean;
  skuPattern: string;
  lowStock: string;
  shipping: string;
  freeOver: string;
  modules: string[];
  notifyEmail: string;
}

function toForm(shop: Shop): FormState {
  return {
    name: shop.name,
    currency: shop.currency,
    taxInclusive: shop.taxInclusive,
    skuPattern: shop.skuPattern,
    lowStock: String(shop.lowStockThreshold),
    shipping: shop.shipping ? minorToMajorInput(shop.shipping.amount, shop.currency) : '',
    freeOver: shop.shipping?.freeOver ? minorToMajorInput(shop.shipping.freeOver, shop.currency) : '',
    modules: [...shop.modules],
    notifyEmail: shop.notifyEmail ?? '',
  };
}

/** Form → settings patch, or the first problem. */
function toPatch(form: FormState): { ok: true; patch: ShopSettingsPatch } | { ok: false; error: string } {
  const currency = form.currency.trim().toUpperCase();

  if (!form.name.trim()) {
    return { ok: false, error: 'Give your shop a name.' };
  }

  if (!isValidCurrency(currency)) {
    return { ok: false, error: 'Use a three-letter currency code, e.g. INR or USD.' };
  }

  const lowStock = parseCount(form.lowStock);

  if (lowStock === null) {
    return { ok: false, error: 'The low-stock threshold is a whole number, e.g. 5.' };
  }

  let shipping: ShopSettingsPatch['shipping'] = null;

  if (form.shipping.trim()) {
    const amount = parseMoney(form.shipping, currency, { allowZero: true });

    if (amount === null) {
      return { ok: false, error: `Shipping is an amount in ${currency}, or blank for none.` };
    }

    const freeOver = form.freeOver.trim() ? parseMoney(form.freeOver, currency) : null;

    if (form.freeOver.trim() && freeOver === null) {
      return { ok: false, error: 'Free shipping over is an amount, or blank.' };
    }

    shipping = { amount, freeOver };
  }

  const email = form.notifyEmail.trim();

  if (email && !isValidEmail(email)) {
    return { ok: false, error: 'Enter a valid email for order notifications.' };
  }

  return {
    ok: true,
    patch: {
      name: form.name.trim(),
      currency,
      taxInclusive: form.taxInclusive,
      skuPattern: form.skuPattern.trim() || '{PRODUCT}-{OPTION1}-{OPTION2}',
      lowStockThreshold: lowStock,
      shipping,
      modules: SHOP_MODULES.filter((m) => form.modules.includes(m)),
      notifyEmail: email || null,
    },
  };
}

export function ShopSettingsView({
  appId,
  shop,
  onSaved,
}: {
  appId: string;
  shop: Shop;
  onSaved: (next: Shop) => void;
}) {
  const [form, setForm] = useState<FormState>(() => toForm(shop));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setForm(toForm(shop)), [shop]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(shop));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = toPatch(form);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      onSaved(await apiUpdateShopSettings(appId, result.patch));
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save your shop settings.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (id: string) =>
    set('modules', form.modules.includes(id) ? form.modules.filter((m) => m !== id) : [...form.modules, id]);

  return (
    <form onSubmit={save} className="space-y-6">
      <Section title="Store" icon="i-ph:storefront">
        <div className={classNames(CARD, 'space-y-3 p-4')}>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              <Field label="Store name">
                <input value={form.name} onChange={(e) => set('name', e.target.value)} className="ui-input" />
              </Field>
            </div>
            <div className="w-24">
              <Field label="Currency">
                <input
                  value={form.currency}
                  onChange={(e) => set('currency', e.target.value.toUpperCase().slice(0, 3))}
                  maxLength={3}
                  spellCheck={false}
                  className="ui-input-mono uppercase"
                />
              </Field>
            </div>
          </div>
          {form.currency.trim().toUpperCase() !== shop.currency && (
            <p className="text-[11px] leading-relaxed text-warning">
              Currency can only change before you add products: prices are not converted.
            </p>
          )}
          <Switch checked={form.taxInclusive} onChange={(v) => set('taxInclusive', v)} label="Prices include tax" />
          <Field label="Order notifications" hint="Where new-order emails go.">
            <input
              type="email"
              value={form.notifyEmail}
              onChange={(e) => set('notifyEmail', e.target.value)}
              placeholder="you@example.com"
              className="ui-input"
            />
          </Field>
        </div>
      </Section>

      <Section title="Inventory" icon="i-ph:stack">
        <div className={classNames(CARD, 'space-y-3 p-4')}>
          <Field
            label="SKU pattern"
            hint={
              <>
                Tokens {'{PRODUCT} {OPTION1} {OPTION2} {OPTION3} {N}'}. Example:{' '}
                <code className="font-geist-mono text-secondary">
                  {previewSku(form.skuPattern, { product: 'Café Tee', options: ['Navy', 'M'], n: 1 })}
                </code>
                . Applies to new variants only.
              </>
            }
          >
            <input
              value={form.skuPattern}
              onChange={(e) => set('skuPattern', e.target.value)}
              spellCheck={false}
              className="ui-input-mono"
            />
          </Field>
          <Field label="Low-stock alert at" hint="Tracked variants at or below this count show in Inventory.">
            <input
              value={form.lowStock}
              onChange={(e) => set('lowStock', e.target.value)}
              inputMode="numeric"
              className="ui-input-mono !w-24"
            />
          </Field>
        </div>
      </Section>

      <Section title="Shipping" icon="i-ph:truck">
        <div className={classNames(CARD, 'space-y-3 p-4')}>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              <Field label={`Flat rate (${form.currency || shop.currency})`} hint="Blank for no shipping charge.">
                <input
                  value={form.shipping}
                  onChange={(e) => set('shipping', e.target.value)}
                  inputMode="decimal"
                  placeholder="None"
                  className="ui-input-mono !py-2"
                />
              </Field>
            </div>
            <div className="min-w-0 flex-1">
              <Field label="Free over" hint="Optional.">
                <input
                  value={form.freeOver}
                  onChange={(e) => set('freeOver', e.target.value)}
                  inputMode="decimal"
                  placeholder="Never"
                  disabled={!form.shipping.trim()}
                  className="ui-input-mono !py-2 disabled:opacity-50"
                />
              </Field>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Modules" icon="i-ph:puzzle-piece">
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {SHOP_MODULES.map((id) => (
            <label key={id} className="flex cursor-pointer items-start gap-2.5 px-4 py-2.5">
              <input
                type="checkbox"
                checked={form.modules.includes(id)}
                onChange={() => toggleModule(id)}
                className="mt-0.5 accent-accent"
              />
              <span className="min-w-0">
                <span className="block text-[12px] text-primary">{moduleLabel(id)}</span>
                <span className="block text-[11px] leading-relaxed text-muted">{SHOP_MODULE_DESCRIPTIONS[id]}</span>
              </span>
            </label>
          ))}
        </div>
      </Section>

      <div className="sticky bottom-0 -mx-4 space-y-2 border-t border-hairline bg-surface px-4 py-3">
        {error && <ErrorBanner message={error} />}
        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving || !dirty} className={BTN_SECONDARY}>
            {saving && <Spinner />}
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {saved && !dirty && <span className="text-[11px] text-muted">Saved</span>}
          {dirty && !saving && <span className="text-[11px] text-muted">Unsaved changes</span>}
        </div>
      </div>
    </form>
  );
}
